// Taktik: ein Rätsel nach dem anderen, nahe der eigenen Wertung, wahlweise zu einem Thema. Der Gegner zieht
// zuerst; jeder eigene Zug muss der Lösung entsprechen (beim letzten Zug zählt jedes Matt). Der erste Fehler
// zählt für die Wertung, danach darf man weiter probieren oder sich die Lösung zeigen lassen.
import { Chess, normalizeMove } from 'chessops/chess';
import { chessgroundDests } from 'chessops/compat';
import { makeFen, parseFen } from 'chessops/fen';
import { makeUci, parseSquare, parseUci } from 'chessops/util';
import type { Key } from '@lichess-org/chessground/types';
import { call } from './api';
import { buzz } from './appfeel';
import { loadPref, savePref } from './prefs';
import { loadRating, markSeen, record } from './puzzlerating';

export interface Puzzle {
  id: string;
  fen: string;
  moves: string[];
  rating: number;
  themes: string[];
}

export type PuzzleState = 'loading' | 'play' | 'wrong' | 'solved' | 'shown' | 'error';

const position = (fen: string) => Chess.fromSetup(parseFen(fen).unwrap()).unwrap();

export class PuzzleCtrl {
  puzzle: Puzzle | null = null;
  pos = Chess.default();
  /** Index des nächsten Zugs in puzzle.moves */
  ply = 0;
  state: PuzzleState = 'loading';
  last: [Key, Key] | undefined;
  theme: string = loadPref('puzzleTheme', '');
  rating = loadRating();
  /** Thema → Anzahl Rätsel, für die Auswahl */
  counts: Record<string, number> = {};
  hintSquare: Key | null = null;
  error = '';
  /** Ergebnis schon gewertet (erster Fehler oder Lösung) */
  private counted = false;
  private hinted = false;
  private token = 0;

  /** `delay`: Pause vor den Zügen des Gegners in ms (Tests: 0) */
  constructor(readonly redraw: () => void, private readonly delay = 500) {}

  get fen() {
    return makeFen(this.pos.toSetup());
  }

  /** eigene Farbe: die Seite, die nach dem ersten Zug des Gegners am Zug ist */
  get color(): 'white' | 'black' {
    return this.puzzle?.fen.split(' ')[1] === 'w' ? 'black' : 'white';
  }

  get result() {
    return this.counted ? this.rating.history[0] : null;
  }

  dests() {
    const open = this.state === 'play' || this.state === 'wrong';
    return open && this.pos.turn === this.color ? chessgroundDests(this.pos) : new Map();
  }

  /** beim Öffnen der Seite: Themen einmal laden, erstes Rätsel ziehen */
  open() {
    if (!Object.keys(this.counts).length) void call<Record<string, number>>('GET', '/api/puzzle/themes').then(c => ((this.counts = c), this.redraw()), () => {});
    if (!this.puzzle && this.state !== 'error') void this.next();
  }

  setTheme(theme: string) {
    this.theme = theme;
    savePref('puzzleTheme', theme);
    void this.next();
  }

  async next() {
    const token = ++this.token;
    Object.assign(this, { state: 'loading', error: '', hintSquare: null, counted: false, hinted: false });
    this.redraw();
    try {
      const q = new URLSearchParams({ rating: String(this.rating.rating), theme: this.theme, seen: this.rating.seen.slice(-300).join(',') });
      const p = await call<Puzzle>('GET', `/api/puzzle?${q}`);
      if (token !== this.token) return;
      this.rating = markSeen(this.rating, p.id);
      Object.assign(this, { puzzle: p, pos: position(p.fen), ply: 0, last: undefined });
      this.redraw();
      await this.opponent(token);
    } catch (e) {
      if (token === this.token) Object.assign(this, { state: 'error', error: (e as Error).message });
    }
    this.redraw();
  }

  /** Zug einheitlich in chessops-Form (Rochade als König schlägt Turm), egal ob e1g1 oder e1h1 */
  private uci(u: string) {
    return makeUci(normalizeMove(this.pos, parseUci(u)!));
  }

  private play(u: string) {
    this.pos.play(normalizeMove(this.pos, parseUci(u)!));
    this.last = [u.slice(0, 2) as Key, u.slice(2, 4) as Key];
    this.ply++;
  }

  async move(orig: Key, dest: Key) {
    const p = this.puzzle;
    if (!p || this.dests().size === 0) return;
    const expected = p.moves[this.ply];
    const pawn = this.pos.board.get(parseSquare(orig)!)?.role === 'pawn' && /[18]/.test(dest[1]);
    const promo = pawn ? (expected.startsWith(orig + dest) && expected[4]) || 'q' : '';
    const mine = this.uci(orig + dest + promo);
    const finalMate = this.ply === p.moves.length - 1 && (() => { const t = this.pos.clone(); t.play(parseUci(mine)!); return t.isCheckmate(); })();
    if (mine !== this.uci(expected) && !finalMate) {
      if (!this.counted) this.finish(false);
      this.state = 'wrong';
      this.redraw();
      return;
    }
    this.play(mine);
    this.hintSquare = null;
    if (this.ply >= p.moves.length) {
      if (!this.counted) this.finish(true);
      this.state = 'solved';
      buzz([30, 60, 30]);
      this.redraw();
      return;
    }
    this.state = 'play';
    this.redraw();
    await this.opponent(this.token);
  }

  hint() {
    if (!this.puzzle || this.dests().size === 0) return;
    this.hinted = true;
    this.hintSquare = this.puzzle.moves[this.ply].slice(0, 2) as Key;
    this.redraw();
  }

  /** Lösung bis zum Ende vorspielen */
  async reveal() {
    const p = this.puzzle, token = this.token;
    if (!p) return;
    if (!this.counted) this.finish(false);
    this.state = 'shown';
    this.hintSquare = null;
    while (this.ply < p.moves.length && token === this.token) {
      this.play(p.moves[this.ply]);
      this.redraw();
      if (this.delay) await new Promise(r => setTimeout(r, this.delay));
    }
  }

  private finish(win: boolean) {
    const p = this.puzzle!;
    this.counted = true;
    this.rating = record(this.rating, p.id, p.rating, win, !this.hinted);
  }

  /** Zug des Gegners aus der Lösung, mit kurzer Pause */
  private async opponent(token: number) {
    if (this.delay) await new Promise(r => setTimeout(r, this.delay));
    if (token !== this.token || !this.puzzle || this.ply >= this.puzzle.moves.length) return;
    this.play(this.puzzle.moves[this.ply]);
    if (this.state === 'loading') this.state = 'play';
    this.redraw();
  }
}
